/**
 * OperationalBundleDropzone
 *
 * File upload / drag-and-drop for OperationalBundleV1 JSON files.
 *
 * INVARIANTS:
 * - Read-only file ingestion
 * - No backend upload
 * - No execution authority
 * - JSON validation on client only
 */

import React, { useCallback } from 'react';
import { NEUTRAL, TYPOGRAPHY } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface OperationalBundleDropzoneProps {
  onBundleLoaded: (bundleJson: unknown) => void;
  onError: (message: string) => void;
}

export const OperationalBundleDropzone: React.FC<OperationalBundleDropzoneProps> = ({
  onBundleLoaded,
  onError,
}) => {
  const handleFile = useCallback((file: File) => {
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      onError('Only JSON files are supported');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = JSON.parse(text);
        onBundleLoaded(parsed);
      } catch {
        onError('Invalid JSON file');
      }
    };
    reader.onerror = () => onError('Failed to read file');
    reader.readAsText(file);
  }, [onBundleLoaded, onError]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 48,
        border: `2px dashed ${NEUTRAL.border}`,
        borderRadius: 8,
        backgroundColor: NEUTRAL.bg200,
        color: NEUTRAL.text400,
        cursor: 'pointer',
        transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
      }}
      onClick={() => document.getElementById('bundle-file-input')?.click()}
    >
      <div style={{
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
      }}>
        Operational Bundle Viewer
      </div>
      <div style={{
        fontSize: TYPOGRAPHY.body.size,
        textAlign: 'center',
        maxWidth: 400,
        lineHeight: 1.5,
      }}>
        Drag and drop an operational bundle JSON file here, or click to browse.
        <br />
        <span style={{ fontSize: TYPOGRAPHY.label.size, color: NEUTRAL.text500 }}>
          Independent verification · No backend required · proofOnly-safe
        </span>
      </div>
      <input
        id="bundle-file-input"
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
      <div style={{
        padding: '8px 20px',
        border: `1px solid ${NEUTRAL.border}`,
        borderRadius: 4,
        fontSize: TYPOGRAPHY.label.size,
        fontWeight: TYPOGRAPHY.label.weight,
        color: NEUTRAL.text500,
        backgroundColor: NEUTRAL.bg300,
      }}>
        Select Bundle File
      </div>
    </div>
  );
};
